// Refusals that say "not now" instead of "not this file": the request took too
// long (408) or there were too many of them (429). They are retried like a 5xx.
const RETRYABLE_REFUSALS = [408, 429]

// Uploads files one at a time, in the order they were added, and keeps going
// when the connection is bad: a phone in a warehouse drops requests all the time.
//
// `upload(file)` does the actual request and resolves { ok, status, body }; it may
// also throw, which is how a network failure looks. What happens next:
//   2xx           the item is "done"
//   4xx           the server refused the file, so it is "rejected" and not tried again,
//                 except 408 and 429 (see RETRYABLE_REFUSALS), which are about the moment
//   5xx / throw   tried again after a pause (`delays`, the last one repeats), at
//                 most `retries` times, and then "failed" until retry(id) is called
//
// An item is { id, file, status, body } with status one of "pending", "uploading",
// "done", "failed" or "rejected"; `body` is what the server answered last.
// `onChange(item)` is called whenever an item's status changes.
export function uploadQueue({ upload, retries = 3, delays = [1000, 3000, 9000], onChange = () => {} }) {
  const items = []
  let nextId = 1
  let draining = false
  let current = Promise.resolve()

  function setStatus(item, status) {
    item.status = status
    onChange(item)
  }

  async function attempt(item) {
    setStatus(item, "uploading")

    for (let tries = 0; ; tries++) {
      let response = null
      try { response = await upload(item.file) } catch { /* a network failure: same as a 5xx */ }

      if (response) item.body = response.body
      if (response?.ok) return setStatus(item, "done")
      if (response?.status >= 400 && response.status < 500 && !RETRYABLE_REFUSALS.includes(response.status)) return setStatus(item, "rejected")
      if (tries >= retries) return setStatus(item, "failed")

      await new Promise((resolve) => setTimeout(resolve, delays[Math.min(tries, delays.length - 1)] ?? 0))
    }
  }

  function drain() {
    if (draining) return
    draining = true
    current = (async () => {
      try {
        for (let item; (item = items.find((candidate) => candidate.status === "pending")); ) await attempt(item)
      } finally {
        draining = false
      }
    })()
  }

  return {
    items,

    add(file) {
      const item = { id: nextId++, file, status: "pending", body: null }
      items.push(item)
      onChange(item)
      drain()
      return item
    },

    // Only a failed file can be tried again; a rejected one will be refused again.
    retry(id) {
      const item = items.find((candidate) => candidate.id === id)
      if (item?.status !== "failed") return
      setStatus(item, "pending")
      drain()
    },

    // Resolves when everything added so far has been settled.
    idle: () => current
  }
}
